import { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Alert,
  Box,
  CircularProgress,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { RootState, AppDispatch } from '../../store/store';
import { setDraftDrone } from '../../store/slices/missionPlannerSlice';
import {
  clearDroneGeofence,
  fetchDroneGeofence,
} from '../../store/slices/geofenceSlice';
import { useGeofenceViolations } from '../../hooks/useGeofenceViolations';
import { describeBand } from '../../utils/geofence';
import { droneLabel } from '../../types/drone';

/** Value used for "no drone", since a MUI select cannot hold null directly. */
const NO_DRONE = '';

/**
 * Choose the drone a draft is planned for, and report how the draft stands
 * against that drone's geofence (issue #451).
 *
 * Picking a drone is what opts the draft into enforcement: a geofence is
 * assigned per drone, so with none chosen there is no boundary to check
 * against and the draft saves exactly as it always did.
 */
export const DroneGeofenceControls: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const droneId = useSelector((s: RootState) => s.missionPlanner.droneId);
  const drones = useSelector((s: RootState) => s.drones.drones);
  const geofence = useSelector((s: RootState) => s.geofence.geofence);
  const status = useSelector((s: RootState) => s.geofence.status);
  const loadError = useSelector((s: RootState) => s.geofence.error);
  const { enforced, homeBaseOutside, outsideWaypointIds, band, outsideAltitudes } =
    useGeofenceViolations();

  // Counted separately from the altitude band: a waypoint is somewhere on the
  // map the operator can go and look at, and a cruise height is not.
  const positionsOutside = outsideWaypointIds.size + (homeBaseOutside ? 1 : 0);

  // Keep the loaded fence in step with the chosen drone.
  useEffect(() => {
    if (droneId === null) {
      dispatch(clearDroneGeofence());
      return;
    }
    dispatch(fetchDroneGeofence(droneId));
  }, [droneId, dispatch]);

  const droneList = Object.values(drones);

  return (
    <Box sx={{ mb: 2 }}>
      <TextField
        select
        fullWidth
        size="small"
        label="Plan for drone"
        value={droneId ?? NO_DRONE}
        onChange={(e) =>
          dispatch(setDraftDrone(e.target.value === NO_DRONE ? null : e.target.value))
        }
        helperText="Assigning a drone checks the route against its geofence."
      >
        <MenuItem value={NO_DRONE}>
          <em>No drone — route not checked</em>
        </MenuItem>
        {droneList.map((drone) => (
          <MenuItem key={drone.id} value={drone.id}>
            {droneLabel(drone)}
          </MenuItem>
        ))}
      </TextField>

      {droneId !== null && status === 'loading' && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
          <CircularProgress size={14} />
          <Typography variant="caption" color="text.secondary" role="status">
            Loading geofence…
          </Typography>
        </Box>
      )}

      {/* An unloadable fence looks exactly like an absent one on the map, so
          the difference has to be said out loud rather than left to inference. */}
      {status === 'error' && loadError && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          {loadError}
        </Alert>
      )}

      {droneId !== null && status === 'loaded' && !geofence && (
        <Alert severity="info" sx={{ mt: 1 }}>
          This drone has no geofence — the route is not restricted.
        </Alert>
      )}

      {status === 'loaded' && geofence && !enforced && (
        <Alert severity="info" sx={{ mt: 1 }}>
          {geofence.inclusion_rule === 'exclude'
            ? `“${geofence.name}” is a no-fly zone, which is not enforced against a plan.`
            : `“${geofence.name}” is inactive, so the route is not restricted.`}
        </Alert>
      )}

      {enforced && positionsOutside === 0 && (
        <Alert severity="success" sx={{ mt: 1 }}>
          Route is inside “{geofence?.name}”.
        </Alert>
      )}

      {enforced && positionsOutside > 0 && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {positionsOutside} position{positionsOutside === 1 ? '' : 's'} outside “
          {geofence?.name}” — this draft cannot be saved.
          {outsideWaypointIds.size > 0 &&
            ` Marked on the map: ${outsideWaypointIds.size} waypoint${
              outsideWaypointIds.size === 1 ? '' : 's'
            }.`}
          {homeBaseOutside && ' The home base is outside too.'}
        </Alert>
      )}

      {/* The altitude band stands on its own (#541). Its ceiling is regulatory
          rather than drawn, so this is shown with no drone chosen and with no
          fence loaded — the one limit that is never absent. */}
      {outsideAltitudes.length > 0 ? (
        <Alert severity="error" sx={{ mt: 1 }}>
          The flight profile leaves the permitted altitude band ({describeBand(band)})
          — this draft cannot be saved. {outsideAltitudes.join('; ')}.
        </Alert>
      ) : (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', mt: 1 }}
        >
          {/* The caveat is the operator-facing half of #543: this ceiling is
              measured from where the aircraft took off, and the law measures it
              from the ground beneath the aircraft. The system cannot see terrain,
              so the pilot — who is the accountable party — is told plainly, and
              can apply judgement the system has no data for. No issue number
              here; it means nothing to the person flying. */}
          Altitude allowed: {describeBand(band)}. Over uneven ground your height
          above the surface will differ — terrain is not accounted for.
        </Typography>
      )}
    </Box>
  );
};
